import React, { useState } from 'react'
import Box from '@material-ui/core/Box'
import truncate from 'lodash/truncate'
import pick from 'lodash/pick'
import DonutLargeIcon from '@material-ui/icons/DonutLarge'
import SubjectIcon from '@material-ui/icons/Subject'

import { Hider } from '../HOCs/Hider'
import { CueCard, CueCardItem, CueCardTitle } from '../CueCard'
import { CardContent, CardHeading } from '../layout/Card'
import { R } from '../../utils/currency'
import { convertAssetCurrencyToZar } from '../../utils/currency/conversions'
import { getLongDateFromDate } from '../../utils/dateParser'
import { humanizeString } from '../../utils/stringMinipulation'
import { Allocations } from '../Allocations'

export const AssetCard = ({ asset, Footer, supportedCurrencies, highlighted, hideAssetType }) => {
  const [showAllocations, setShowAllocations] = useState(false)
  return (
    <CueCard
      highlighted={highlighted}
      isNew={asset.new}
      warning={asset.flagged_for_deletion}
    >
      <AssetCardHeading
        hideAssetType={hideAssetType}
        onClick={() => setShowAllocations(!showAllocations)}
        asset={asset}
        showAllocations={showAllocations}
      />
      <CardContent>
        <AssetName asset={asset} />
        <Hider
          show={!showAllocations}
          render={() => <AssetDetails asset={asset} supportedCurrencies={supportedCurrencies} />}
        />
        <Hider
          show={showAllocations}
          render={() => <AssetAllocations asset={asset} />}
        />
      </CardContent>
      {Footer &&
        <Footer />
      }
    </CueCard>
  )
}

const AssetCardHeading = ({ asset, showAllocations, onClick, hideAssetType }) =>
  <CardHeading>
    <Box
      display='flex'
      justifyContent='space-between'
      alignItems='center'
    >
      {hideAssetType ? <div /> :
        <h5>{humanizeString(asset.asset_type)}</h5>
      }
      <Box display='flex'>
        {asset.new &&
          <div className='new-asset' >new</div>
        }
        <ToggleAllocations onClick={onClick} showAllocations={showAllocations} />
      </Box>
    </Box>
  </CardHeading>

const AssetDetails = ({ asset, supportedCurrencies }) => {
  return (
    <>
      <CueCardItem
        name={`Current Value`}
        value={R(convertAssetCurrencyToZar(asset.gross_value, supportedCurrencies[asset.currency])).format()}
      />
      <CueCardItem
        name={`Contributions (p/m)`}
        value={R(convertAssetCurrencyToZar(asset.monthly_contribution, supportedCurrencies[asset.currency])).format()}
      />
      <CueCardItem
        name={`Income (p/m)`}
        value={R(convertAssetCurrencyToZar(asset.monthly_income, supportedCurrencies[asset.currency])).format()}
      />
      <CueCardItem
        name={`Date`}
        value={getLongDateFromDate(asset.grouping_identifier ? asset.price_date : asset.updated_at)}
      />
      {asset.return_rate &&
        <CueCardItem
          name={`Return Rate`}
          value={`${asset.return_rate}%`}
        />
      }
      {asset.contract_number &&
        <CueCardItem
          name={`Contract Number`}
          value={truncate(asset.contract_number, { "length": 15 })}
        />
      }
    </>
  )
}

const AssetAllocations = ({ asset }) =>
  <>
    <CardContent>
      <Allocations assetAllocationsDataset={[assetAllocationsFor(asset)]} hideLegend />
    </CardContent>
  </>

const ToggleAllocations = ({ showAllocations, onClick }) =>
  showAllocations
    ? <SubjectIcon onClick={onClick} style={{ cursor: 'pointer' }} />
    : <DonutLargeIcon onClick={onClick} style={{ cursor: 'pointer' }} />

const AssetName = ({ asset }) =>
  <CueCardTitle>
    <span className={asset.grouping_identifier ? 'gci-icon-link' : 'gci-icon-unlink'} />
    <span>{truncate(asset.name, { 'length': 18 })}</span>
  </CueCardTitle>

const assetAllocationsFor = (asset) =>
  pick(asset, [
    'domestic_equity',
    'domestic_property',
    'domestic_bonds',
    'domestic_cash',
    'offshore_equity',
    'offshore_property',
    'offshore_bonds',
    'offshore_cash'
  ])
